import { addCursorLoader, api, currentYear, removeCursorLoader, url } from 'dmui_components'
import useReportTypeMgmt from '../composables/useReportTypeMgmt.js'

const { getBaseToMetaTypeMapping, getReportFlagsToAppend, metaToBaseTypeMapping } = useReportTypeMgmt()

class Report {
	constructor(fields) {
		this.report_id = fields?.report_id
		this.document_title = fields?.document_title
		this.document_id = fields?.document_id
		this.org_name = fields?.org_name
		this.document_year = fields?.document_year
		this.status = fields?.status
		this.created_at = fields?.created_at
		this.updated_at = fields?.updated_at
		this.updated_by = fields?.updated_by || 'Anonymous'
		this.country_name = fields?.country_name
		this.industry_name = fields?.industry_name
		this.region_name = fields?.region_name
		this.document_type = getBaseToMetaTypeMapping(fields?.document_type, fields?.is_csrd, fields?.is_integrated)
		this.status_description = fields?.status_description
	}
}

class ReportRepository {
	constructor() {
		this._abortController = undefined
	}

	_parseReportToPost(report) {
		return {
			document_type: metaToBaseTypeMapping[report.document_type]?.document_type,
			document_year: report.document_year,
			org_id: report.org?.org_id,
			document_url: report.document_url,
			sections: report.sections,
			published_at: report.published_at?.split('.')[0],
			...getReportFlagsToAppend(report.document_type)
		}
	}

	_parseReportToPut(report, section_diffs) {
		return {
			document_id: report.document_id,
			document_title: report.document_title,
			report_id: report.report_id,
			sections: report.sections,
			section_diffs: section_diffs,
			...getReportFlagsToAppend(report.document_type)
		}
	}

	_parseFetchedReport(report) {
		return {
			...report,
			published_at: new Date(report?.published_at),
			document_type: getBaseToMetaTypeMapping(report?.document_type, report?.is_csrd, report?.is_integrated)
		}
	}

	_stringifyParams({ filters, search, order }) {
		const stringFilters = {}
		for (const key in filters) {
			if (filters[key]?.length) {
				stringFilters[key] = typeof filters[key][0] === 'object' ? filters[key].map((f) => f[key]) : [...filters[key]]
				if (key === 'document_type') {
					filters[key].forEach((type, index) => {
						stringFilters.document_type[index] = { ...metaToBaseTypeMapping[type] }
					})
				}
			}
		}

		return JSON.stringify({
			filters: stringFilters,
			search_parameters: search || {},
			order: order || {}
		})
	}

	__abortAndResetAbortController() {
		if (this._abortController) {
			this._abortController.abort()
			this._abortController = undefined
		}
		this._abortController = new AbortController()
	}

	async fetchReportsFromApi(modifiers = {}) {
		const stringParams = this._stringifyParams(modifiers)
		const result = []
		try {
			this.__abortAndResetAbortController()
			addCursorLoader()
			const response = await api(url`/sources_report?body_string=${stringParams}`, { abortController: this._abortController })
			for (const report of response) {
				result.push(new Report(report))
			}
		} catch (e) {
			console.error('Error fetching reports', e)
		} finally {
			removeCursorLoader()
		}
		return result
	}

	async postReport({ report, file }) {
		const formData = new FormData()
		formData.append('report', JSON.stringify({ ...this._parseReportToPost(report) }))
		if (file) {
			formData.append('file', file)
		}

		return await api('sources_report', {
			method: 'POST',
			headers: undefined,
			body: formData
		})
	}

	async putReport({ report, section_diffs }) {
		return await api('sources_report', {
			method: 'PUT',
			body: { ...this._parseReportToPut(report, section_diffs) }
		})
	}

	async deleteReport(documentTitle) {
		try {
			addCursorLoader()
			await api(url`/sources_report?document_title=${documentTitle}`, { method: 'DELETE' })
		} catch (e) {
			console.error('Error deleting report', e)
		} finally {
			removeCursorLoader()
		}
	}

	async retryReport(report_id, document_id, document_title) {
		return await api('sources_report/retry', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ report_id: report_id, document_id: document_id, document_title: document_title })
		})
	}

	async getReportDetails(report_id, documentType) {
		const report_data = await api(url`/sources_report/${report_id}?document_type=${documentType}`)
		return this._parseFetchedReport(report_data)
	}

	getLatestYear() {
		return new Date().getMonth() === 11 ? Number(currentYear) + 1 : Number(currentYear)
	}
}

export default new ReportRepository()
